// ═══════════════════════════════════════════════════════════════
// Shelter Management Portal — Mobile-first field worker view
// Route: /sheltermanagement
// Auth: existing GAB login, no redirect — self-contained
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { useShelterStore } from '../../../store/shelterStore';
import type { Shelter } from '../../../types/shelter';
import {
  IconLayoutDashboard, IconUserPlus, IconUsers, IconPackage,
  IconChevronDown, IconLogout, IconX, IconTransfer, IconAlertTriangle,
} from '@tabler/icons-react';
import './ShelterPortal.css';
import ShelterPortalDashboard from './ShelterPortalDashboard';
import ShelterPortalIntake from './ShelterPortalIntake';
import ShelterPortalRoster from './ShelterPortalRoster';
import ShelterPortalDistribution from './ShelterPortalDistribution';
import ShelterPortalTransfer from './ShelterPortalTransfer';
import ShelterPortalReport from './ShelterPortalReport';

type Tab = 'dashboard' | 'intake' | 'roster' | 'distribution' | 'transfer' | 'report';

const TABS: { id: Tab; label: string; Icon: (p: { size?: number }) => JSX.Element | null }[] = [
  { id: 'dashboard',    label: 'Overview',  Icon: IconLayoutDashboard },
  { id: 'intake',       label: 'Intake',    Icon: IconUserPlus },
  { id: 'roster',       label: 'Roster',    Icon: IconUsers },
  { id: 'transfer',     label: 'Transfer',  Icon: IconTransfer },
  { id: 'distribution', label: 'Supplies',  Icon: IconPackage },
  { id: 'report',       label: 'Report',    Icon: IconAlertTriangle },
];

const SHELTER_KEY = 'sp-selected-shelter';

// ── Login screen ─────────────────────────────────────────────

function PortalLogin() {
  const { login } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('Email and password are required.'); return; }
    setLoading(true);
    setError('');
    try {
      await login(email.trim(), password);
    } catch (err: any) {
      setError(err?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sp-login-wrap">
      <div className="sp-login-card">
        {/* Header — matches PSP suite bar wordmark style */}
        <div className="sp-login-logo-row">
          <img
            src="/cds/assets/logo/OpenGov-Logo-Blurple.svg"
            alt="OpenGov"
            className="sp-login-og-logo"
          />
          <div className="sp-login-divider" />
          <span className="sp-login-product-name">Emergency Management</span>
        </div>
        <div className="sp-login-title">Shelter Portal</div>
        <div className="sp-login-sub">Field worker access — sign in to continue</div>

        <form onSubmit={submit}>
          {error && <div className="sp-error">{error}</div>}
          <div className="sp-field">
            <label className="sp-label">Email</label>
            <input className="sp-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com" autoComplete="email" inputMode="email" />
          </div>
          <div className="sp-field">
            <label className="sp-label">Password</label>
            <input className="sp-input" type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="••••••••" autoComplete="current-password" />
          </div>
          <button type="submit" className="sp-btn sp-btn-primary" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Shelter picker drawer ─────────────────────────────────────

function ShelterPicker({ shelters, current, onSelect, onClose }: {
  shelters: Shelter[];
  current: Shelter | null;
  onSelect: (s: Shelter) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const filtered = search
    ? shelters.filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
    : shelters;

  return (
    <div className="sp-drawer-overlay" onClick={onClose}>
      <div className="sp-drawer" onClick={e => e.stopPropagation()}>
        <div className="sp-drawer-handle" />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="sp-drawer-title" style={{ marginBottom: 0 }}>Select Shelter</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7B7D7F' }}>
            <IconX size={20} />
          </button>
        </div>
        <input className="sp-input" placeholder="Search shelters…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ marginBottom: 16 }} />
        {filtered.map(s => {
          const pct = s.capacity ? Math.round((s.currentPopulation / s.capacity) * 100) : 0;
          const barColor = pct >= 95 ? '#B12525' : pct >= 80 ? '#f59e0b' : '#4B3FFF';
          return (
            <div key={s.id} className={`sp-picker-item ${current?.id === s.id ? 'selected' : ''}`} onClick={() => onSelect(s)}>
              <div style={{ flex: 1 }}>
                <div className="sp-picker-name">{s.name}</div>
                <div className="sp-picker-meta">{s.address}{s.city ? `, ${s.city}` : ''}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <div style={{ flex: 1, height: 4, background: '#e2e8f0', borderRadius: 99 }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 99 }} />
                  </div>
                  <span style={{ fontSize: 12, color: '#7B7D7F', whiteSpace: 'nowrap' }}>
                    {s.currentPopulation}/{s.capacity}
                  </span>
                </div>
              </div>
              <span className={`sp-badge ${s.status === 'open' ? 'sp-badge-green' : 'sp-badge-gray'}`}>
                {s.status.replace('_', ' ')}
              </span>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', color: '#939598', fontSize: 14, padding: '24px 0' }}>
            No shelters found.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main portal ───────────────────────────────────────────────

export default function ShelterPortal() {
  const { isAuthenticated, logout } = useAuthStore();
  const { shelters, survivors, families, supplies, loadShelters, loadSurvivors, fetchFamilies, loadSupplies } = useShelterStore();

  const [tab, setTab] = useState<Tab>('dashboard');
  const [showPicker, setShowPicker] = useState(false);
  const [selectedShelterId, setSelectedShelterId] = useState<number | null>(() => {
    const v = localStorage.getItem(SHELTER_KEY);
    return v ? Number(v) : null;
  });

  useEffect(() => {
    loadShelters();
    loadSurvivors();
    fetchFamilies();
    loadSupplies();
  }, [loadShelters, loadSurvivors, fetchFamilies, loadSupplies]);

  useEffect(() => {
    if (!selectedShelterId && shelters.length > 0) {
      const first = shelters.find(s => s.status === 'open') ?? shelters[0];
      setSelectedShelterId(first.id);
      localStorage.setItem(SHELTER_KEY, String(first.id));
    }
  }, [shelters, selectedShelterId]);

  const shelter = useMemo(
    () => shelters.find(s => s.id === selectedShelterId) ?? null,
    [shelters, selectedShelterId],
  );

  const selectShelter = (s: Shelter) => {
    setSelectedShelterId(s.id);
    localStorage.setItem(SHELTER_KEY, String(s.id));
    setShowPicker(false);
  };

  if (!isAuthenticated) return <PortalLogin />;

  return (
    <div className="sp-shell">
      {/* Top bar — white chrome, matches PSP suite bar style */}
      <div className="sp-topbar">
        <img
          src="/cds/assets/logo/OpenGov-Logo-Blurple.svg"
          alt="OpenGov"
          className="sp-topbar-logo"
        />
        <div className="sp-topbar-divider" />
        <span className="sp-topbar-product">Shelter</span>

        <button className="sp-shelter-btn" onClick={() => setShowPicker(true)}>
          <span>{shelter ? shelter.name : 'Select shelter…'}</span>
          <IconChevronDown size={12} style={{ flexShrink: 0 }} />
        </button>

        <button
          onClick={logout}
          className="sp-topbar-icon-btn"
          title="Sign out"
        >
          <IconLogout size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="sp-content">
        {!shelter ? (
          <div style={{ textAlign: 'center', padding: '60px 16px', color: '#939598' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🏛</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: '#1B1C1C' }}>No shelter selected</div>
            <div style={{ fontSize: 14, marginBottom: 24 }}>Tap the button above to choose a shelter.</div>
            <button className="sp-btn sp-btn-primary" style={{ maxWidth: 200, margin: '0 auto' }} onClick={() => setShowPicker(true)}>
              Select Shelter
            </button>
          </div>
        ) : tab === 'dashboard' ? (
          <ShelterPortalDashboard shelter={shelter} onIntake={() => setTab('intake')} />
        ) : tab === 'intake' ? (
          <ShelterPortalIntake shelter={shelter} />
        ) : tab === 'roster' ? (
          <ShelterPortalRoster shelter={shelter} />
        ) : tab === 'transfer' ? (
          <ShelterPortalTransfer shelter={shelter} allShelters={shelters} />
        ) : tab === 'report' ? (
          <ShelterPortalReport shelter={shelter} />
        ) : (
          <ShelterPortalDistribution shelter={shelter} />
        )}
      </div>

      {/* Bottom nav */}
      <nav className="sp-bottom-nav">
        {TABS.map(t => (
          <button key={t.id} className={`sp-nav-btn${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
            <t.Icon size={20} />
            {t.label}
          </button>
        ))}
      </nav>

      {/* Shelter picker */}
      {showPicker && (
        <ShelterPicker
          shelters={shelters}
          current={shelter}
          onSelect={selectShelter}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
